import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import { Layout } from "shared/utils";
import styles from "./styles/autorizationPayments.module.scss";
import { formatCurrency } from "shared/utils";
import { IExportedPackage } from "./types";
import { ExpandMore, Info, Calculate, Receipt, Payments, Payment, Visibility, KeyboardArrowLeft, Edit, ContentCopy } from "@mui/icons-material";
import Image from "shared/assets/index";
import {
  columnsConceptsDeductions,
  ConceptsDeductionsRow,
} from "./helper/columnsConceptsDeductions";
import {
  columnsPackageInvoices,
  PackageInvoicesRow,
} from "./helper/columnsPackageInvoices";
import tableStyles from "../../pages/read-documents/styles/tableResults.module.scss";
import { FooterTable } from "shared/components/table/components/FooterTable";
import {
  downloadPaymentPackage,
  downloadDraftResolution,
  updatePaymentPackage,
  getPaymentByElectronicDocument,
  downloadPaymentAttachments,
  Attachment,
} from "shared/api/services";
import { downloadDocument } from "shared/utils/helpers/downloadDocument";
import { RouterGuard } from "shared/components";
import { ModalDocumentsPdf } from "shared/components/ModalDocuments.pdf";
import { accesRoles } from "shared/models";
import { usePaymentPackage } from "./hooks/usePaymentPackage";
import { useDetailPaymentPackage } from "./hooks/useDetailPaymentPackage";
import { usePackageInvoices } from "./hooks/usePackageInvoices";
import { FormRevertExport } from "./components/ModalRevertExport";
import { ModalCargarInformacionPago } from "./components/ModalCargarInformacionPago";
import { useDispatch } from "react-redux";
import { Dispatch, Spinner } from "shared";
import { IPaymentByElectronicDocument } from "../detailed-consultation/Models/IPaymentInformation";
import Swal from "sweetalert2";

interface LocationState {
  package?: IExportedPackage;
}

interface EditableFieldWithMenuProps {
  rowId: string;
  field: "cdp" | "rcp";
  value: string;
  onChange: (rowId: string, field: "cdp" | "rcp", value: string) => void;
  onApplyToEmpty: (rowId: string, field: "cdp" | "rcp") => void;
}

const EditableFieldWithMenu: React.FC<EditableFieldWithMenuProps> = ({
  rowId,
  field,
  value,
  onChange,
  onApplyToEmpty,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApplyToEmpty = () => {
    onApplyToEmpty(rowId, field);
    handleClose();
  };

  return (
    <>
      <TextField
        value={value}
        onChange={(e) => onChange(rowId, field, e.target.value)}
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ marginLeft: "0px", marginRight: "-22px" }}>
              <IconButton
                onClick={handleClick}
                sx={{
                  padding: "0px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "16px !important",
                    width: "16px !important",
                    height: "16px !important",
                  },
                }}
              >
                <ContentCopy />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          fontSize: "0.75rem",
          "& .MuiOutlinedInput-root": {
            height: "31px",
          },
          "& .MuiInputBase-input": {
            fontSize: "0.75rem",
            padding: "4px 8px",
          },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "150px",
          },
          "& .MuiMenuItem-root": {
            padding: "0px 12px",
            fontSize: "12px",
          },
        }}
      >
        <MenuItem onClick={handleApplyToEmpty}>
          Replicar a todas las filas
        </MenuItem>
      </Menu>
    </>
  );
};

export const ViewExportedPackage = () => {
  const navigate = useNavigate();
  const { packageId } = useParams();
  const { state } = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useDispatch<Dispatch>();
  const [isEditingConcepts, setIsEditingConcepts] = useState(false);
  const [editingConcepts, setEditingConcepts] = useState<Record<string, { cdp: string; rcp: string }>>({});
  const [originalConcepts, setOriginalConcepts] = useState<Record<string, { cdp: string; rcp: string }>>({});

  const { packageData: apiPackageData, refetch: refetchPackage } = usePaymentPackage(packageId);
  const packageData: IExportedPackage | null = apiPackageData || (state as LocationState | null)?.package || null;
  const isOpenActions = Boolean(anchorEl);

  const {
    conceptsDeductions,
    totalElements: totalConceptsElements,
    page: conceptsPage,
    size: conceptsSize,
    handleChangePage: handleConceptsChangePage,
    handleChangeSize: handleConceptsChangeSize,
    refetch: refetchConcepts,
  } = useDetailPaymentPackage(packageId);

  const {
    packageInvoices,
    totalElements: totalInvoicesElements,
    page: invoicesPage,
    size: invoicesSize,
    handleChangePage: handleInvoicesChangePage,
    handleChangeSize: handleInvoicesChangeSize,
    refetch: refetchInvoices,
  } = usePackageInvoices(packageData?.electronicDocumentIds);

  const [paymentInfo, setPaymentInfo] = useState<IPaymentByElectronicDocument | null>(null);
  const [paymentInfoLoading, setPaymentInfoLoading] = useState(false);
  const [paymentAttachmentsCache, setPaymentAttachmentsCache] = useState<Attachment[] | null>(null);
  const [paymentPdfLoading, setPaymentPdfLoading] = useState(false);
  const [paymentDocumentPdf, setPaymentDocumentPdf] = useState<any>({});
  const [openPaymentPdf, setOpenPaymentPdf] = useState(false);

  const firstElectronicDocumentId = packageData?.electronicDocumentIds?.[0];
  const isPackagePaid = packageData?.statePackage?.name === "Pagado";

  useEffect(() => {
    if (!firstElectronicDocumentId || !isPackagePaid) {
      setPaymentInfo(null);
      return;
    }

    const fetchPaymentInfo = async () => {
      setPaymentInfoLoading(true);
      try {
        const response = await getPaymentByElectronicDocument(
          firstElectronicDocumentId.toString()
        );
        setPaymentInfo(response.data);
      } catch (error) {
        console.error("Error fetching package payment info:", error);
        setPaymentInfo(null);
      } finally {
        setPaymentInfoLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [firstElectronicDocumentId, isPackagePaid]);

  const fetchPaymentAttachments = useCallback(async () => {
    if (paymentAttachmentsCache) {
      return paymentAttachmentsCache;
    }
    if (!firstElectronicDocumentId) {
      return [];
    }

    const response = await downloadPaymentAttachments({
      electronicDocumentId: firstElectronicDocumentId,
    });
    setPaymentAttachmentsCache(response.data.attachments);
    return response.data.attachments;
  }, [firstElectronicDocumentId, paymentAttachmentsCache]);

  const handleViewPaymentAttachment = async (attachmentId: number) => {
    setPaymentPdfLoading(true);
    try {
      const attachments = await fetchPaymentAttachments();
      const attachment = attachments.find((att) => att.id === attachmentId);

      if (!attachment || !attachment.fileContentBase64) {
        return;
      }

      setPaymentDocumentPdf(attachment.fileContentBase64);
      setOpenPaymentPdf(true);
    } catch (error) {
      console.error("Error viewing payment attachment:", error);
    } finally {
      setPaymentPdfLoading(false);
    }
  };

  const valuesResultsPages = [50, 100, 250, 500];

  const totalPagos = conceptsDeductions
    .filter((row) => row.signo === "+")
    .reduce((sum, row) => sum + row.valor, 0);

  const totalDeducciones = conceptsDeductions
    .filter((row) => row.signo === "-")
    .reduce((sum, row) => sum + row.valor, 0);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleOpenActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActions = () => {
    setAnchorEl(null);
  };

  const handleRevertExport = () => {
    handleCloseActions();
    if (!packageData?.paymentConsecutive) return;

    const onClose = () => {
      dispatch.messageModal.cleanStateMessage();
    };

    const onSuccess = () => {
      handleGoBack();
    };

    dispatch.messageModal.setMessageModal({
      show: true,
      title: "Revertir exporte de pago",
      onClose() {
        onClose();
      },
      size: "medium",
      description: React.createElement(FormRevertExport, {
        onClose,
        onSuccess,
        consecutive: packageData.paymentConsecutive,
      }),
    });
  };

  const handleDownloadZip = async () => {
    handleCloseActions();
    try {
      const response = await downloadPaymentPackage({
        consecutive: packageId || "",
      });
      downloadDocument(
        response,
        "Descarga exitosa",
        "El archivo ZIP se ha descargado correctamente",
        "success"
      );
    } catch (error) {
      console.error("Error downloading ZIP:", error);
    }
  };

  const handleDownloadDraftResolution = async () => {
    handleCloseActions();
    try {
      const response = await downloadDraftResolution({
        consecutive: packageId || "",
      });
      downloadDocument(
        response,
        "Descarga exitosa",
        "La preforma de resolución se ha descargado correctamente",
        "success"
      );
    } catch (error) {
      console.error("Error downloading draft resolution:", error);
    }
  };

  const handleLoadPaymentInfo = () => {
    handleCloseActions();

    const onClose = () => {
      dispatch.messageModal.cleanStateMessage();
    };

    const onSuccess = () => {
      refetchPackage();
      refetchConcepts();
      refetchInvoices();
    };

    // Process CDP values from conceptsDeductions
    const cdpValues = conceptsDeductions
      .map((row) => row.certificadoPresupuestal)
      .filter((value) => value && value.trim() !== "");
    
    const uniqueCdpValues = Array.from(new Set(cdpValues));
    const numeroCDP = uniqueCdpValues.length > 0
      ? uniqueCdpValues.join(", ")
      : "";

    // Process RCP values from conceptsDeductions
    const rcpValues = conceptsDeductions
      .map((row) => row.rcp)
      .filter((value) => value && value.trim() !== "");
    
    const uniqueRcpValues = Array.from(new Set(rcpValues));
    const numeroRPC = uniqueRcpValues.length > 0
      ? uniqueRcpValues.join(", ")
      : "";

    const readOnlyData = {
      numeroFacturas: packageData?.invoicesCount?.toString() || "",
      codigoPrestador: packageData?.nit || "",
      valorPagado: packageData?.totalExportValue?.toString() || "",
      numeroCDP,
      numeroRPC,
    };

    dispatch.messageModal.setMessageModal({
      show: true,
      title: "Cargar información de pago",
      onClose() {
        onClose();
      },
      size: "1200px",
      description: React.createElement(ModalCargarInformacionPago, {
        onClose,
        onSuccess,
        packageData: packageData || undefined,
        readOnlyData,
        electronicDocumentIds: packageData?.electronicDocumentIds,
      }),
    });
  };

  const handleToggleEditMode = () => {
    if (isEditingConcepts) {
      setIsEditingConcepts(false);
      setEditingConcepts({});
      setOriginalConcepts({});
    } else {
      setIsEditingConcepts(true);
      const initialEditingState: Record<string, { cdp: string; rcp: string }> = {};
      const originalState: Record<string, { cdp: string; rcp: string }> = {};
      conceptsDeductions.forEach((row) => {
        initialEditingState[row.rowId] = {
          cdp: row.certificadoPresupuestal || "",
          rcp: row.rcp || "",
        };
        originalState[row.rowId] = {
          cdp: row.certificadoPresupuestal || "",
          rcp: row.rcp || "",
        };
      });
      setEditingConcepts(initialEditingState);
      setOriginalConcepts(originalState);
    }
  };

  const handleSaveConcepts = async () => {
    const modifiedItems = conceptsDeductions
      .filter((row) => {
        const currentCdp = editingConcepts[row.rowId]?.cdp || "";
        const currentRcp = editingConcepts[row.rowId]?.rcp || "";
        const originalCdp = originalConcepts[row.rowId]?.cdp || "";
        const originalRcp = originalConcepts[row.rowId]?.rcp || "";
        return currentCdp !== originalCdp || currentRcp !== originalRcp;
      })
      .map((row) => ({
        id: row.id,
        budgetCertificate: editingConcepts[row.rowId]?.cdp || "",
        budgetRegistration: editingConcepts[row.rowId]?.rcp || "",
      }));

    if (modifiedItems.length === 0) {
      setIsEditingConcepts(false);
      setEditingConcepts({});
      setOriginalConcepts({});
      return;
    }

    try {
      await updatePaymentPackage({ items: modifiedItems });
      Swal.fire({
        icon: "success",
        title: "Actualización exitosa",
        text: "Los datos de CDP y RPC se han actualizado correctamente",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
      });
      await refetchConcepts();
      setIsEditingConcepts(false);
      setEditingConcepts({});
      setOriginalConcepts({});
    } catch (error) {
      console.error("Error updating payment package:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo actualizar los datos. Por favor intente nuevamente.",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#d32f2f",
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditingConcepts(false);
    setEditingConcepts({});
  };

  const handleConceptChange = (rowId: string, field: "cdp" | "rcp", value: string) => {
    setEditingConcepts((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [field]: value,
      },
    }));
  };

  const handleApplyConceptToEmpty = (rowId: string, field: "cdp" | "rcp") => {
    const valueToApply = editingConcepts[rowId]?.[field] || "";
    setEditingConcepts((prev) => {
      const newState = { ...prev };
      conceptsDeductions.forEach((row) => {
        if (row.rowId !== rowId) {
          const currentValue = newState[row.rowId]?.[field] || "";
          if (!currentValue.trim()) {
            newState[row.rowId] = {
              ...newState[row.rowId],
              [field]: valueToApply,
            };
          }
        }
      });
      return newState;
    });
  };

  return (
    <Layout>
      <section className={styles.contentAutorizationPayment}>
        <Box className={styles.header}>
          <Box
            sx={{ display: "flex", alignItems: "center", marginRight: "auto", cursor: "pointer" }}
            onClick={handleGoBack}
            data-testid="buttonBack"
          >
            <KeyboardArrowLeft sx={{ color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)" }} />
            <Typography sx={{ fontSize: "1rem", ml: 0.01, color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)", fontWeight: 500 }}>Volver</Typography>
          </Box>
          <Box className={styles.headerRight}>
            <Box className={styles.actions}>
              <img src={Image.iconoEditar} alt="Edit" />
              <IconButton
                data-testid="buttonActions"
                onClick={handleOpenActions}
              >
                Acciones <ExpandMore />
              </IconButton>
            </Box>
            <Menu
              id="menu-actions"
              data-testid="menuActions"
              anchorEl={anchorEl}
              open={isOpenActions}
              onClose={handleCloseActions}
              MenuListProps={{
                "aria-labelledby": "basic-button",
                sx: { padding: "10px 0" },
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                style: {
                  marginLeft: "-60px",
                },
              }}
            >
              {packageData?.statePackage?.name !== "Pagado" && (
                <MenuItem onClick={handleRevertExport} data-testid="menu-item-revert" sx={{ fontSize: "13px !important", width: "100%", paddingLeft: "16px !important", paddingRight: "16px !important", paddingTop: "5px !important", paddingBottom: "5px !important" }}>
                  Revertir exporte de pago
                </MenuItem>
              )}
              {packageData?.statePackage?.name !== "Pagado" && (
                <MenuItem onClick={handleLoadPaymentInfo} data-testid="menu-item-load-payment-info" sx={{ fontSize: "13px !important", width: "100%", paddingLeft: "16px !important", paddingRight: "16px !important", paddingTop: "5px !important", paddingBottom: "5px !important" }}>
                  Cargar información de pago
                </MenuItem>
              )}
              <MenuItem onClick={handleDownloadZip} data-testid="menu-item-download" sx={{ fontSize: "13px !important", width: "100%", paddingLeft: "16px !important", paddingRight: "16px !important", paddingTop: "5px !important", paddingBottom: "5px !important" }}>
                Descargar ZIP del paquete de pago
              </MenuItem>
              <RouterGuard roles={accesRoles.finance} type="component">
                <MenuItem onClick={handleDownloadDraftResolution} data-testid="menu-item-download-draft-resolution" sx={{ fontSize: "13px !important", width: "100%", paddingLeft: "16px !important", paddingRight: "16px !important", paddingTop: "5px !important", paddingBottom: "5px !important" }}>
                  Descargar Preforma Resolución
                </MenuItem>
              </RouterGuard>
            </Menu>
          </Box>
        </Box>

        <Card
          sx={{
            border: "1px solid #a1a1a1",
            borderRadius: "12px",
            margin: "0.5rem 0",
            opacity: 1,
            width: "100%",
            padding: "8px 8px 20px 8px",
            flexShrink: 0,
          }}
        >
          <Box sx={{ padding: "8px 8px 4px 8px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Info sx={{ color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)", fontSize: "30px" }} />
            <Typography className="titleMainLayout" sx={{ alignSelf: "center", lineHeight: "32px", paddingBottom: "0 !important", fontSize: "1.25rem !important" }}>Detalle de Exporte de Pagos</Typography>
          </Box>
          <Grid
            container
            spacing={2}
            sx={{ padding: "8px 12px 12px 12px", alignItems: "flex-end" }}
          >
            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Consecutivo de pago:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {packageData?.paymentConsecutive ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  NIT prestador de salud:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {packageData?.nit ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Prestador de salud:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {packageData?.healthProvider ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Cantidad de facturas exportadas:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {packageData?.invoicesCount ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Total exporte de pago:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {packageData?.totalExportValue
                    ? formatCurrency(packageData.totalExportValue)
                    : "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Fecha de exporte:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {packageData?.exportDate ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Usuario que generó el exporte:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {packageData?.exportUser ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Estado de exporte de pago:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {packageData?.statePackage?.name ?? packageData?.exportStatus ?? "-"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>

        <Card
          sx={{
            border: "1px solid #a1a1a1",
            borderRadius: "12px",
            margin: "0.5rem 0",
            opacity: 1,
            width: "100%",
            padding: "8px 12px 20px 12px",
            flexShrink: 0,
          }}
        >
          <Box sx={{ padding: "8px 8px 12px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Calculate sx={{ color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)", fontSize: "30px" }} />
              <Typography className="titleMainLayout" sx={{ alignSelf: "center", lineHeight: "32px", paddingBottom: "0 !important", fontSize: "1.25rem !important" }}>Conceptos y deducciones</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: "8px" }}>
              {!isEditingConcepts ? (
                <IconButton
                  onClick={handleToggleEditMode}
                  disabled={packageData?.statePackage?.name === "Pagado"}
                  sx={{
                    borderColor: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                    color: packageData?.statePackage?.name === "Pagado" ? "#ccc" : "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelEdit}
                    sx={{
                      fontSize: "0.8rem",
                      padding: "4px 12px",
                      border: "1px solid #d32f2f",
                      color: "#d32f2f",
                      "&:hover": {
                        border: "1px solid #d32f2f",
                        color: "#d32f2f",
                      },
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveConcepts}
                    sx={{
                      fontSize: "0.8rem",
                      padding: "4px 12px",
                      backgroundColor: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 0.8)",
                      },
                    }}
                  >
                    Guardar
                  </Button>
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ flex: 1, maxHeight: "40vh", overflow: "auto" }}>
            <Box className={tableStyles.containerTableResults}>
              <TableContainer
                className={tableStyles.tableResults}
                sx={{ overflowX: "auto", maxHeight: "calc(40vh - 60px)" }}
              >
                <Table sx={{ tableLayout: "fixed", width: "100%" }} stickyHeader>
                  <TableHead
                    className={tableStyles.tablaHeader}
                    sx={{
                      backgroundColor: "#eef0f3 !important",
                      background: "#eef0f3 !important",
                      "& .MuiTableCell-root": {
                        backgroundColor: "#eef0f3 !important",
                      },
                    }}
                  >
                    <TableRow>
                      {columnsConceptsDeductions.map((column, index) => (
                        <TableCell
                          key={column.field}
                          className={
                            index === columnsConceptsDeductions.length - 1
                              ? undefined
                              : tableStyles.tableCell
                          }
                          sx={{
                            backgroundColor: "#eef0f3",
                            border: 0,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color:
                              "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                            padding: "6px",
                            textTransform: "capitalize",
                            whiteSpace: "normal",
                            lineHeight: "1.1",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {column.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conceptsDeductions
                      .map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            background:
                              "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                            },
                          }}
                        >
                          {columnsConceptsDeductions.map((column, index) => (
                            <TableCell
                              key={column.field}
                              className={
                                index === columnsConceptsDeductions.length - 1
                                  ? undefined
                                  : tableStyles.tableCell
                              }
                              sx={{
                                color: "#6d6d6d",
                                fontSize:
                                  "clamp(0.75rem, 0.3042rem + 1.48vw, 0.675rem)",
                                fontWeight: 500,
                                padding: "4px 6px",
                                width: `${100 / columnsConceptsDeductions.length}%`,
                              }}
                            >
                              {column.field === "valor" ? (
                                formatCurrency(
                                  row[column.field as keyof ConceptsDeductionsRow] as number
                                )
                              ) : isEditingConcepts && (column.field === "certificadoPresupuestal" || column.field === "rcp") ? (
                                <EditableFieldWithMenu
                                  rowId={row.rowId}
                                  field={column.field === "certificadoPresupuestal" ? "cdp" : "rcp"}
                                  value={editingConcepts[row.rowId]?.[column.field === "certificadoPresupuestal" ? "cdp" : "rcp"] || ""}
                                  onChange={handleConceptChange}
                                  onApplyToEmpty={handleApplyConceptToEmpty}
                                />
                              ) : (
                                row[column.field as keyof ConceptsDeductionsRow] as string
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <FooterTable
                handleChangePage={(_event, newPage) => handleConceptsChangePage(newPage)}
                handleChangeSizePage={(event) => handleConceptsChangeSize(event.target.value)}
                numberPage={conceptsPage}
                size={conceptsSize}
                sizeRows={conceptsSize.toString()}
                totalElements={totalConceptsElements}
                valuesResultsPages={valuesResultsPages}
                contentSize={conceptsDeductions.length}
              />
            </Box>
          </Box>
        </Card>

        <Card
          sx={{
            border: "1px solid #a1a1a1",
            borderRadius: "12px",
            margin: "0.5rem 0",
            opacity: 1,
            width: "100%",
            padding: "8px 12px 20px 12px",
            flexShrink: 0,
          }}
        >
          <Box sx={{ padding: "8px 8px 12px 8px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Receipt sx={{ color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)", fontSize: "28px" }} />
            <Typography className="titleMainLayout" sx={{ alignSelf: "center", lineHeight: "32px", paddingBottom: "0 !important", fontSize: "1.25rem !important" }}>Facturas del paquete</Typography>
          </Box>
          <Box sx={{ flex: 1, maxHeight: "40vh", overflow: "auto" }}>
            <Box className={tableStyles.containerTableResults}>
              <TableContainer
                className={tableStyles.tableResults}
                sx={{ overflowX: "auto", maxHeight: "calc(40vh - 60px)" }}
              >
                <Table sx={{ tableLayout: "fixed", width: "100%" }} stickyHeader>
                  <TableHead
                    className={tableStyles.tablaHeader}
                    sx={{
                      backgroundColor: "#eef0f3 !important",
                      background: "#eef0f3 !important",
                      "& .MuiTableCell-root": {
                        backgroundColor: "#eef0f3 !important",
                      },
                    }}
                  >
                    <TableRow>
                      {columnsPackageInvoices.map((column, index) => (
                        <TableCell
                          key={column.field}
                          className={
                            index === columnsPackageInvoices.length - 1
                              ? undefined
                              : tableStyles.tableCell
                          }
                          sx={{
                            backgroundColor: "#eef0f3",
                            border: 0,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color:
                              "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                            padding: "6px",
                            textTransform: "capitalize",
                            whiteSpace: "normal",
                            lineHeight: "1.1",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {column.headerName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packageInvoices
                      .map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            background:
                              "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                            },
                          }}
                        >
                          {columnsPackageInvoices.map((column, index) => (
                            <TableCell
                              key={column.field}
                              className={
                                index === columnsPackageInvoices.length - 1
                                  ? undefined
                                  : tableStyles.tableCell
                              }
                              sx={{
                                color: "#6d6d6d",
                                fontSize:
                                  "clamp(0.75rem, 0.3042rem + 1.48vw, 0.675rem)",
                                fontWeight: 500,
                                padding: "8px 6px",
                                width: `${100 / columnsPackageInvoices.length}%`,
                              }}
                            >
                              {column.field === "totalFactura" ||
                              column.field === "valorAvalado" ||
                              column.field === "valorGlosaAceptada" ||
                              column.field === "valorEnNoAcuerdo" ? (
                                formatCurrency(
                                  (row[column.field as keyof PackageInvoicesRow] as number) || 0
                                )
                              ) : (
                                row[column.field as keyof PackageInvoicesRow] as string
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <FooterTable
                handleChangePage={(_event, newPage) => handleInvoicesChangePage(newPage)}
                handleChangeSizePage={(event) => handleInvoicesChangeSize(event.target.value)}
                numberPage={invoicesPage}
                size={invoicesSize}
                sizeRows={invoicesSize.toString()}
                totalElements={totalInvoicesElements}
                valuesResultsPages={valuesResultsPages}
                contentSize={packageInvoices.length}
              />
            </Box>
          </Box>
        </Card>

        <Card
          sx={{
            border: "1px solid #a1a1a1",
            borderRadius: "12px",
            margin: "0.5rem 0 0 0",
            opacity: 1,
            width: "100%",
            padding: "8px 8px 20px 8px",
            flexShrink: 0,
          }}
        >
          <Box sx={{ padding: "8px 8px 4px 8px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Payments sx={{ color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)", fontSize: "30px" }} />
            <Typography className="titleMainLayout" sx={{ alignSelf: "center", lineHeight: "32px", paddingBottom: "0 !important", fontSize: "1.25rem !important" }}>Resumen financiero</Typography>
          </Box>
          <Grid
            container
            spacing={2}
            sx={{ padding: "8px 12px 12px 12px", alignItems: "flex-end" }}
          >
            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Total pagos:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {formatCurrency(totalPagos)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Total deducciones:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {formatCurrency(totalDeducciones)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {isPackagePaid && (
        <Card
          sx={{
            border: "1px solid #a1a1a1",
            borderRadius: "12px",
            margin: "0.5rem 0 0 0",
            opacity: 1,
            width: "100%",
            padding: "8px 8px 20px 8px",
            flexShrink: 0,
          }}
        >
          <Box sx={{ padding: "8px 8px 4px 8px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Payment sx={{ color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)", fontSize: "30px" }} />
            <Typography className="titleMainLayout" sx={{ alignSelf: "center", lineHeight: "32px", paddingBottom: "0 !important", fontSize: "1.25rem !important" }}>Resumen información del pago</Typography>
          </Box>
          <Grid
            container
            spacing={2}
            sx={{ padding: "8px 12px 12px 12px", alignItems: "flex-end" }}
          >
            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  N° doc pago (TD):
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {paymentInfo?.treasurySequence ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Valor Pagado:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {paymentInfo?.paymentTotalValue
                    ? formatCurrency(paymentInfo.paymentTotalValue)
                    : "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  N° Resolución:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {paymentInfo?.paymentResolution ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Rubro:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {paymentInfo?.paymentCategory ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  Fecha de Pago:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#969696",
                    fontWeight: 500,
                    paddingLeft: "5px",
                  }}
                >
                  {paymentInfo?.paymentAt ?? "-"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  PDF Del Documento de Pago (TD):
                </Typography>
                {paymentInfo?.paymentAttachmentId ? (
                  <IconButton
                    onClick={() => handleViewPaymentAttachment(paymentInfo.paymentAttachmentId)}
                    data-testid="open-payment-pdf-button"
                    size="small"
                    sx={{
                      padding: "0 0 0 5px",
                      color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                    }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#969696",
                      fontWeight: 500,
                      paddingLeft: "5px",
                    }}
                  >
                    -
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={3}>
              <Box
                sx={{ paddingBottom: "8px", borderBottom: "1px solid #a1a1a1" }}
              >
                <Typography
                  sx={{
                    fontSize: "0.81rem",
                    color: "#6D6D6D",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    padding: "0.5rem 0 1rem",
                    width: "83%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  PDF Del Documento de Resolución:
                </Typography>
                {paymentInfo?.resolutionAttachmentId ? (
                  <IconButton
                    onClick={() => handleViewPaymentAttachment(paymentInfo.resolutionAttachmentId!)}
                    data-testid="open-payment-resolution-pdf-button"
                    size="small"
                    sx={{
                      padding: "0 0 0 5px",
                      color: "rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)",
                    }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#969696",
                      fontWeight: 500,
                      paddingLeft: "5px",
                    }}
                  >
                    -
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
          {paymentInfoLoading && <Spinner />}
        </Card>
        )}

        {openPaymentPdf && (
          <ModalDocumentsPdf
            document={paymentDocumentPdf}
            openIframePdf={openPaymentPdf}
            handleOpenIframePdf={() => setOpenPaymentPdf(false)}
          />
        )}
        {paymentPdfLoading && <Spinner />}

        <Box />

        {!packageData && (
          <Box sx={{ py: 4 }}>
            <Typography variant="body1" sx={{ color: "#6d6d6d" }}>
              No se encontró información del paquete seleccionado.
            </Typography>
            {packageId && (
              <Typography
                variant="body2"
                sx={{ color: "#969696", mt: 1 }}
              >
                Id solicitado: {packageId}
              </Typography>
            )}
          </Box>
        )}
      </section>
    </Layout>
  );
};
